import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  FiRefreshCw, FiSearch, FiTrendingUp, FiUsers, FiUserPlus,
  FiMessageSquare, FiCopy, FiCheck, FiAward, FiMapPin, FiX,
} from 'react-icons/fi'
import { useAuth } from '../contexts/AuthContext'

// Aba "Pareto" do Recrutamento (2026-07-14). Materializa o estudo 80/20: uma
// linha por grupo com a pessoa responsável (líder/empresária) ranqueada pelo
// score composto de vendas YTD, recrutas YTD e atividade WhatsApp. É a lista
// de contato da diretoria — cada linha tem o WhatsApp da pessoa. Proximidade
// geográfica é a próxima etapa (fora daqui por enquanto).

interface RankItem {
  rank: number
  grupoCodigo: string
  grupoNome: string | null
  nome: string | null
  codigo: string | null
  tipo: '1' | '2' | null
  celular: string | null
  cidade: string | null
  distrito: string | null
  vendas: number
  recrutas: number
  leadsWa: number
  waTotal: number
  waRespondidos: number
  tier: 'A' | 'B' | 'C' | null
  tierRate: number | null
  score: number
  latitude: number | null
  longitude: number | null
  distanceKm?: number | null
  vendasShare?: number
  recrutasShare?: number
  whatsappShare?: number
  inCutVendas?: boolean
  inCutRecrutas?: boolean
  inCutWhatsapp?: boolean
}
interface CutInfo { total: number; items: number; cutCount: number; cutPct: number }
interface RankingData {
  success: boolean
  year: number
  computedAt: string
  cuts: { vendas: CutInfo; recrutas: CutInfo; whatsapp: CutInfo }
  items: RankItem[]
  ref?: { cep: string; city: string | null; state: string | null }
  refError?: string
}

type SortKey = 'score' | 'vendas' | 'recrutas' | 'whatsapp' | 'distancia'

const fmtBRL = (v: number) =>
  v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })

const waDigits = (celular: string | null): string | null => {
  const d = String(celular || '').replace(/\D/g, '')
  if (d.length < 10) return null
  return d.startsWith('55') && d.length >= 12 ? d : `55${d}`
}

// neu-tag: o fundo passa a ser derivado do proprio currentColor misturado na
// base neu (o tint /15 foi calibrado sobre branco e fica barrento). A cor de
// texto continua mandando — por isso bg e text tem que ficar JUNTOS aqui.
const TIER_STYLE: Record<string, string> = {
  A: 'neu-tag bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
  B: 'neu-tag bg-amber-500/15 text-amber-600 dark:text-amber-400',
  C: 'neu-tag bg-red-500/15 text-red-500',
}

export function ParetoRankingPanel() {
  const { authenticatedFetch } = useAuth()
  const [data, setData] = useState<RankingData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [sortKey, setSortKey] = useState<SortKey>('score')
  const [onlyCut, setOnlyCut] = useState(false)
  const [copied, setCopied] = useState<string | null>(null)
  // Proximidade: CEP de referência → backend anota distanceKm por contato.
  const [cepInput, setCepInput] = useState('')
  const cepRef = cepInput.replace(/\D/g, '')
  const cepValid = cepRef.length === 8

  const load = useCallback(async (force = false) => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      if (force) params.set('force', '1')
      if (cepValid) params.set('cep', cepRef)
      const qs = params.toString()
      const r = await authenticatedFetch(`/api/pareto/ranking${qs ? `?${qs}` : ''}`)
      const d = await r.json()
      if (!r.ok || !d.success) throw new Error(d.error || `HTTP ${r.status}`)
      setData(d)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Falha ao carregar ranking')
    } finally {
      setLoading(false)
    }
  }, [authenticatedFetch, cepValid, cepRef])

  useEffect(() => { load() }, [load])

  const hasDistance = !!data?.ref
  // Sem referência, "Mais próximos" não faz sentido — volta pro score.
  useEffect(() => { if (!hasDistance && sortKey === 'distancia') setSortKey('score') }, [hasDistance, sortKey])

  const items = useMemo(() => {
    if (!data) return []
    let list = [...data.items]
    if (onlyCut) list = list.filter(i => i.inCutVendas || i.inCutRecrutas || i.inCutWhatsapp)
    const q = search.trim().toLowerCase()
    if (q) {
      list = list.filter(i =>
        (i.nome || '').toLowerCase().includes(q) ||
        (i.grupoNome || '').toLowerCase().includes(q) ||
        (i.cidade || '').toLowerCase().includes(q) ||
        (i.distrito || '').toLowerCase().includes(q))
    }
    if (sortKey === 'distancia') {
      // Mais próximos primeiro; sem coordenada vai pro fim.
      list.sort((a, b) => (a.distanceKm ?? Infinity) - (b.distanceKm ?? Infinity))
      return list
    }
    const val: Record<Exclude<SortKey, 'distancia'>, (i: RankItem) => number> = {
      score: i => i.score,
      vendas: i => i.vendas,
      recrutas: i => i.recrutas,
      whatsapp: i => i.waRespondidos * 2 + i.leadsWa,
    }
    list.sort((a, b) => val[sortKey](b) - val[sortKey](a))
    return list
  }, [data, search, sortKey, onlyCut])

  const copyPhone = (item: RankItem) => {
    if (!item.celular) return
    navigator.clipboard.writeText(item.celular).then(() => {
      setCopied(item.grupoCodigo)
      setTimeout(() => setCopied(null), 2000)
    })
  }

  const openWa = (item: RankItem) => {
    const d = waDigits(item.celular)
    if (d) window.open(`https://wa.me/${d}`, '_blank')
  }

  const maxScore = items.length ? Math.max(...items.map(i => i.score)) : 100

  return (
    <div className="h-full flex flex-col overflow-hidden neu-forms">
      {/* Header + KPIs — mesmo padrão da Captação (glass-bar, título + ações) */}
      <div className="flex-shrink-0 border-b border-border glass-bar neu-bar px-4 py-3">
        <div className="flex items-center gap-2 mb-3">
          <FiAward className="w-5 h-5 text-violet-500" />
          <h2 className="text-base font-semibold text-foreground">Pareto</h2>
          <span className="text-xs text-muted-foreground hidden sm:inline">
            · quem concentra vendas, recrutamento e WhatsApp — {data?.year ?? new Date().getFullYear()} até agora
          </span>
          <div className="ml-auto flex items-center gap-1.5">
            <button onClick={() => load(true)} disabled={loading} title="Recalcular agora (ignora o cache de 15 min)"
              className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-border neu-convex text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors">
              <FiRefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
          {[
            { label: 'Grupos ranqueados', value: data ? String(data.items.length) : '—', icon: FiUsers, cls: 'text-foreground', tip: 'Grupos com venda, recruta ou lead no ano' },
            { label: '80% das vendas', value: data ? `${data.cuts.vendas.cutCount} grupos` : '—', icon: FiTrendingUp, cls: 'text-emerald-500', tip: data ? `${data.cuts.vendas.cutCount} de ${data.cuts.vendas.items} grupos (${data.cuts.vendas.cutPct}%) fecham 80% de ${fmtBRL(data.cuts.vendas.total)}` : '' },
            { label: '80% dos recrutas', value: data ? `${data.cuts.recrutas.cutCount} grupos` : '—', icon: FiUserPlus, cls: 'text-sky-500', tip: data ? `${data.cuts.recrutas.cutCount} de ${data.cuts.recrutas.items} grupos (${data.cuts.recrutas.cutPct}%) fecham 80% de ${data.cuts.recrutas.total} recrutas` : '' },
            { label: '80% do WhatsApp', value: data ? `${data.cuts.whatsapp.cutCount} líderes` : '—', icon: FiMessageSquare, cls: 'text-violet-500', tip: data ? `${data.cuts.whatsapp.cutCount} de ${data.cuts.whatsapp.items} líderes (${data.cuts.whatsapp.cutPct}%) concentram 80% da atividade WhatsApp` : '' },
          ].map(k => (
            <div key={k.label} title={k.tip} className="rounded-lg border border-border bg-background neu-card px-3 py-2">
              <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                <k.icon className={`w-3.5 h-3.5 ${k.cls}`} />
                <span className="truncate">{k.label}</span>
              </div>
              <div className={`text-lg font-bold tabular-nums ${k.cls}`}>{k.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Sub-barra: busca + ordenação + filtro do corte */}
      <div className="flex-shrink-0 border-b border-border glass-bar neu-bar px-4 py-2 flex flex-wrap items-center gap-2">
        <div className="relative">
          <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar líder, grupo, cidade…"
            className="h-8 w-56 text-xs rounded-md border border-border bg-background pl-8 pr-2 text-foreground placeholder:text-muted-foreground"
          />
        </div>
        <select value={sortKey} onChange={e => setSortKey(e.target.value as SortKey)}
          title="Critério de ordenação do ranking"
          className="h-8 text-xs rounded-md border border-border bg-background px-2 text-foreground">
          <option value="score">Score geral</option>
          <option value="vendas">Mais vendedores</option>
          <option value="recrutas">Mais recrutadores</option>
          <option value="whatsapp">Mais ativos no WhatsApp</option>
          {hasDistance && <option value="distancia">Mais próximos</option>}
        </select>
        <div className="relative" title="Proximidade: digite um CEP de referência pra ver a distância de cada contato e ordenar por quem está mais perto">
          <FiMapPin className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <input
            value={cepInput}
            onChange={e => setCepInput(e.target.value)}
            placeholder="CEP de referência"
            maxLength={9}
            className="h-8 w-36 text-xs rounded-md border border-border bg-background pl-8 pr-6 text-foreground placeholder:text-muted-foreground"
          />
          {cepInput && (
            <button onClick={() => setCepInput('')} title="Limpar CEP de referência"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
              <FiX className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        {data?.ref && (
          <span className="inline-flex items-center gap-1 rounded-full bg-violet-500/10 text-violet-500 px-2 py-0.5 text-[11px] font-medium"
            title={`Distâncias calculadas a partir do CEP ${data.ref.cep}`}>
            <FiMapPin className="w-3 h-3" />
            perto de {data.ref.city || data.ref.cep}{data.ref.state ? `/${data.ref.state}` : ''}
          </span>
        )}
        {cepValid && data?.refError && (
          <span className="text-[11px] text-red-500">{data.refError}</span>
        )}
        <label title="Mostrar só quem está no corte de 80% em pelo menos uma dimensão"
          className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none">
          <input type="checkbox" checked={onlyCut} onChange={e => setOnlyCut(e.target.checked)} className="accent-violet-500" />
          Só o corte 80/20
        </label>
        <span className="ml-auto text-[11px] text-muted-foreground tabular-nums">
          {items.length} {items.length === 1 ? 'linha' : 'linhas'}
        </span>
      </div>

      {/* Lista */}
      <div className="flex-1 overflow-auto neu-table">
        {error && (
          <div className="p-6 text-center text-sm text-red-500">{error}</div>
        )}
        {!error && loading && !data && (
          <div className="p-6 text-center text-sm text-muted-foreground animate-pulse">Calculando ranking…</div>
        )}
        {!error && data && (
          <table className="w-full text-xs min-w-[880px]">
            <thead className="sticky top-0 z-10 bg-card border-b border-border neu-thead">
              <tr className="text-left text-[10px] uppercase tracking-wide text-muted-foreground">
                <th className="px-3 py-2 w-10">#</th>
                <th className="px-2 py-2">Líder / Empresária</th>
                <th className="px-2 py-2 text-right" title="Vendas do grupo no ano (Oracle). Ponto verde = está no grupo que fecha 80% das vendas.">Vendas {data.year}</th>
                <th className="px-2 py-2 text-right" title="Consultoras novas do grupo no ano (semana de lançamento). Ponto azul = corte 80/20 do recrutamento.">Recrutas</th>
                <th className="px-2 py-2" title="Leads recebidos e respondidos no WhatsApp no ano + tier de responsividade (A responde, C silencia). Ponto violeta = corte 80/20 da atividade.">WhatsApp</th>
                <th className="px-2 py-2 w-32" title="Score composto: 40% vendas, 30% recrutas, 30% WhatsApp (percentil em cada dimensão)">Score</th>
                <th className="px-3 py-2 w-20 text-right">Contato</th>
              </tr>
            </thead>
            <tbody>
              {items.map(it => (
                <tr key={it.grupoCodigo} className="border-b border-border/50 hover:bg-muted/30 transition-colors neu-tr">
                  <td className="px-3 py-2 font-bold tabular-nums text-muted-foreground">{it.rank}</td>
                  <td className="px-2 py-2">
                    <div className="flex items-center gap-1.5">
                      <span className="font-semibold text-foreground">{it.nome || it.grupoNome || `Grupo ${it.grupoCodigo}`}</span>
                      {it.tipo && (
                        <span title={it.tipo === '2' ? 'Líder de grupo' : 'Empresária (distrito)'}
                          className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase neu-tag ${it.tipo === '2' ? 'bg-violet-500/15 text-violet-500' : 'bg-orange-500/15 text-orange-500'}`}>
                          {it.tipo === '2' ? 'Líder' : 'Empresária'}
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] text-muted-foreground truncate max-w-[260px]">
                      {[it.grupoNome, it.distrito, it.cidade].filter(Boolean).join(' · ') || '—'}
                      {it.distanceKm != null && (
                        <span className="text-violet-500 font-semibold" title="Distância até o CEP de referência">
                          {' '}· {it.distanceKm.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} km
                        </span>
                      )}
                      {hasDistance && it.distanceKm == null && (
                        <span title="Contato sem endereço geocodificado no cadastro — distância indisponível"> · sem geo</span>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums">
                    <span className="inline-flex items-center gap-1 justify-end">
                      {it.inCutVendas && <span className="w-1.5 h-1.5 rounded-full neu-dot bg-emerald-500 flex-shrink-0" title="No corte: junto com os demais marcados, fecha 80% das vendas" />}
                      <span className="font-semibold text-foreground">{it.vendas > 0 ? fmtBRL(it.vendas) : '—'}</span>
                    </span>
                    {it.vendasShare != null && it.vendas > 0 && (
                      <div className="text-[10px] text-muted-foreground">{it.vendasShare}% do total</div>
                    )}
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums">
                    <span className="inline-flex items-center gap-1 justify-end">
                      {it.inCutRecrutas && <span className="w-1.5 h-1.5 rounded-full neu-dot bg-sky-500 flex-shrink-0" title="No corte: junto com os demais marcados, fecha 80% dos recrutas" />}
                      <span className="font-semibold text-foreground">{it.recrutas || '—'}</span>
                    </span>
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex items-center gap-1.5">
                      {it.inCutWhatsapp && <span className="w-1.5 h-1.5 rounded-full neu-dot bg-violet-500 flex-shrink-0" title="No corte: junto com os demais marcados, concentra 80% da atividade WhatsApp" />}
                      <span className="tabular-nums text-foreground" title={`${it.leadsWa} leads recebidos no ano · ${it.waRespondidos} follow-ups respondidos`}>
                        {it.leadsWa > 0 || it.waRespondidos > 0 ? `${it.waRespondidos}/${it.leadsWa}` : '—'}
                      </span>
                      {it.tier && (
                        <span title={`Tier ${it.tier} de responsividade${it.tierRate != null ? ` (responde ${Math.round(it.tierRate * 100)}% dos lembretes)` : ''}`}
                          className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${TIER_STYLE[it.tier]}`}>
                          {it.tier}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex items-center gap-1.5" title={`Score ${it.score} de 100 (40% vendas, 30% recrutas, 30% WhatsApp)`}>
                      <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden neu-gauge">
                        <div className="h-full rounded-full bg-violet-500" style={{ width: `${maxScore ? (it.score / maxScore) * 100 : 0}%` }} />
                      </div>
                      <span className="tabular-nums font-semibold text-foreground w-9 text-right">{it.score}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => openWa(it)} disabled={!waDigits(it.celular)}
                        title={waDigits(it.celular) ? `Abrir conversa no WhatsApp (${it.celular})` : 'Sem celular no cadastro'}
                        className="inline-flex items-center justify-center h-7 w-7 rounded-md border border-border neu-convex text-emerald-500 hover:bg-emerald-500/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                        <FiMessageSquare className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => copyPhone(it)} disabled={!it.celular}
                        title={it.celular ? `Copiar telefone (${it.celular})` : 'Sem telefone no cadastro'}
                        className="inline-flex items-center justify-center h-7 w-7 rounded-md border border-border neu-convex text-muted-foreground hover:text-foreground hover:bg-muted/50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                        {copied === it.grupoCodigo ? <FiCheck className="w-3.5 h-3.5 text-emerald-500" /> : <FiCopy className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">Nenhum resultado pro filtro atual.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
